import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Bot, User, X, MessageCircle } from 'lucide-react';

function AIChat({ isOpen, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    
    // Add user message
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    
    setLoading(true);
    
    try {
      const response = await axios.post('/api/chat', {
        message: userMessage
      });
      
      // Add AI response
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response,
        context_used: response.data.context_used
      }]);
    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const suggestedQuestions = [
    'Will I go to foreign countries?',
    'When will I get married?',
    'What challenges should I watch for?',
    'Which career suits my numbers?'
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: '100%',
            maxWidth: '600px',
            height: '90vh',
            maxHeight: '90vh',
            background: '#151827',
            borderRadius: '20px',
            border: '2px solid #2D3748',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            margin: '0 auto'
          }}
        >
          {/* Header */}
          <div style={{
            padding: '1rem',
            borderBottom: '1px solid #2D3748',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'linear-gradient(135deg, #1A1B2E 0%, #151827 100%)',
            flexShrink: 0
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #00D9FF 0%, #0891B2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Bot size={20} color="#0A0F2A" />
              </div>
              <div style={{ minWidth: 0 }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0 }}>Numerology AI</h3>
                <p style={{ fontSize: '0.75rem', color: '#9CA3AF', margin: 0 }}>Ask me anything about your chart</p>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: '#9CA3AF',
                cursor: 'pointer',
                padding: '0.5rem'
              }}
              data-testid="close-chat"
            >
              <X size={24} />
            </button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            minHeight: 0
          }}>
            {messages.length === 0 && (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <p style={{ color: '#9CA3AF', marginBottom: '1.5rem' }}>Ask me questions based on your numerology profile:</p>
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {suggestedQuestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => setInput(q)}
                      className="btn btn-ghost"
                      style={{ fontSize: '0.875rem', padding: '0.5rem 1rem', textAlign: 'left' }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                  display: 'flex',
                  gap: '0.75rem',
                  alignItems: 'flex-start',
                  flexDirection: msg.role === 'user' ? 'row-reverse' : 'row'
                }}
              >
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: msg.role === 'user' ? '#7C3AED' : 'linear-gradient(135deg, #00D9FF 0%, #0891B2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {msg.role === 'user' ? <User size={18} /> : <Bot size={18} color="#0A0F2A" />}
                </div>
                <div style={{
                  maxWidth: '75%',
                  padding: '0.75rem 1rem',
                  borderRadius: '12px',
                  background: msg.role === 'user' ? '#7C3AED' : '#1E2139',
                  color: '#F9FAFB',
                  fontSize: '0.9375rem',
                  lineHeight: 1.6
                }}>
                  {msg.content}
                  {msg.context_used && (
                    <div style={{ 
                      marginTop: '0.5rem', 
                      fontSize: '0.75rem', 
                      color: '#9CA3AF',
                      fontStyle: 'italic'
                    }}>
                      ✓ Based on your numerology profile
                    </div>
                  )}
                </div>
              </motion.div>
            ))}

            {loading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}
              >
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #00D9FF 0%, #0891B2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Bot size={18} color="#0A0F2A" />
                </div>
                <div style={{
                  padding: '0.75rem 1rem',
                  borderRadius: '12px',
                  background: '#1E2139',
                  color: '#9CA3AF',
                  fontSize: '0.875rem'
                }}>
                  Thinking...
                </div>
              </motion.div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: '1rem',
            borderTop: '1px solid #2D3748',
            background: '#1A1B2E',
            flexShrink: 0
          }}>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about your numerology..."
                data-testid="chat-input"
                style={{
                  flex: 1,
                  padding: '1rem',
                  background: '#0A0F2A',
                  border: '2px solid #374151',
                  borderRadius: '12px',
                  color: '#F9FAFB',
                  fontSize: '1rem',
                  minHeight: '52px',
                  outline: 'none'
                }}
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || loading}
                className="btn btn-primary"
                data-testid="send-chat"
                style={{
                  padding: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '52px',
                  minWidth: '52px',
                  maxWidth: '52px',
                  borderRadius: '12px',
                  flexShrink: 0
                }}
              >
                <Send size={22} />
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default AIChat;
